import { useEffect, useState } from "react";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, RotateCcw, Trash2 } from "lucide-react";

const SERVICE_OPTS = [
  "Administrative Support", "Virtual Assistant Services", "Calendar Management",
  "Scheduling", "Email Management", "Data Entry", "Document Management",
  "Research", "Customer Support", "Project Coordination", "CRM Support",
  "Intake Coordination", "Other",
];
const STATUSES = ["draft", "submitted", "in_review", "assigned", "in_progress", "completed", "cancelled"];

const empty = () => ({
  title: "", services_requested: [], description: "", desired_outcome: "",
  priority: "normal", requested_due_date: "", recurring: false,
  additional_instructions: "", assigned_to: "", staff_role: "", status: "draft",
});

export default function ServiceRequestPanel({ client, recordId, onSaved, onContext }) {
  const { currentOrgId } = useAuth();
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiFetch(`/scheduling/staff?organization_id=${currentOrgId}`).then((r) => {
      setStaff((r?.staff || []).map((m) => ({ id: m.user_id, full_name: m.full_name, role: m.role })));
    }).catch(() => {});
  }, [currentOrgId]);

  useEffect(() => {
    if (!recordId) { setEditing(null); setForm(empty()); return; }
    setLoading(true);
    supabase.from("service_requests").select("*, clients(first_name,last_name,email)").eq("id", recordId).limit(1).maybeSingle()
      .then(({ data: r }) => {
        if (!r) return;
        setEditing(r);
        setForm({
          title: r.title || "", services_requested: r.services_requested || [],
          description: r.description || "", desired_outcome: r.desired_outcome || "",
          priority: r.priority || "normal", requested_due_date: r.requested_due_date || "",
          recurring: !!r.recurring, additional_instructions: r.additional_instructions || "",
          assigned_to: r.assigned_to || "", staff_role: r.staff_role || "", status: r.status || "draft",
        });
      })
      .finally(() => setLoading(false));
  }, [recordId]);

  const save = async (statusOverride) => {
    if (!client) return toast.error("Pick a client first");
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.description.trim()) return toast.error("Description is required");
    if ((form.services_requested || []).length === 0) return toast.error("Select at least one service");
    const payload = {
      organization_id: currentOrgId, client_id: client.id,
      title: form.title.trim(), services_requested: form.services_requested,
      description: form.description.trim(), desired_outcome: form.desired_outcome || null,
      priority: form.priority, requested_due_date: form.requested_due_date || null,
      recurring: form.recurring, additional_instructions: form.additional_instructions || null,
      assigned_to: form.assigned_to || null, staff_role: form.staff_role || null,
      status: statusOverride || form.status,
    };
    try {
      let r;
      if (editing) r = await apiFetch(`/service-requests/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      else r = await apiFetch(`/service-requests`, { method: "POST", body: JSON.stringify(payload) });
      toast.success(editing ? "Service request updated" : (payload.status === "submitted" ? "Request submitted" : "Draft saved"));
      onSaved && onSaved({ kind: "service_request", id: r?.id || editing?.id });
    } catch (e) { toast.error(e.message || "Save failed"); }
  };

  const changeStatus = async (newStatus) => {
    if (!editing) return;
    try {
      const r = await apiFetch(`/service-requests/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r || !r.id) return toast.error("Update was blocked. You may not have permission.");
      toast.success(`Status changed to ${newStatus.replace(/_/g, " ")}`);
      setForm((f) => ({ ...f, status: newStatus }));
      setEditing((e) => (e ? { ...e, status: newStatus } : e));
      onSaved && onSaved({ kind: "service_request", id: editing.id });
    } catch (e) { toast.error(e.message || "Update failed"); }
  };

  const removeRequest = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete service request "${editing.title}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/service-requests/${editing.id}`, { method: "DELETE" });
      toast.success("Service request deleted");
      onSaved && onSaved({ kind: "service_request", id: editing.id, deleted: true });
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    const isCompleted = editing?.status === "completed";
    const isCancelled = editing?.status === "cancelled";
    onContext && onContext({
      client, status: form.status, editing,
      actions: [
        { key: "draft", label: "Save draft", variant: "outline", testid: "sr-save-draft", onClick: () => save("draft") },
        { key: "submit", label: editing ? "Update" : "Submit", variant: "primary", testid: "sr-submit", onClick: () => save(editing ? form.status : "submitted") },
        ...(editing && !isCompleted && !isCancelled ? [
          { key: "complete", label: "Mark complete", icon: CheckCircle2, variant: "outline", testid: "sr-complete", onClick: () => changeStatus("completed") },
        ] : []),
        ...(editing && isCompleted ? [
          { key: "reopen", label: "Reopen", icon: RotateCcw, variant: "outline", testid: "sr-reopen", onClick: () => changeStatus("in_progress") },
        ] : []),
        ...(editing ? [
          { key: "delete", label: "Delete", icon: Trash2, variant: "outline", testid: "sr-delete", onClick: removeRequest },
        ] : []),
      ],
    });
    /* eslint-disable-next-line */
  }, [form, editing]);

  const toggleService = (s) => setForm((f) => ({
    ...f, services_requested: f.services_requested.includes(s) ? f.services_requested.filter((x) => x !== s) : [...f.services_requested, s],
  }));

  if (loading) return <div className="text-center py-16 text-sm text-[#57534E]">Loading request…</div>;

  return (
    <div className="space-y-4" data-testid="workspace-service-request">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#57534E] flex items-center gap-1.5"><ClipboardCheck size={13} /> Service Request</div>
        <div className="text-lg font-semibold font-display">{editing?.request_number || "New Request"} {client && <span className="text-sm text-[#57534E]">— {client.first_name} {client.last_name}</span>}</div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <Field label="Title *"><Input data-testid="sr-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field label="Description *"><Textarea rows={3} data-testid="sr-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <Field label="Desired outcome"><Textarea rows={2} value={form.desired_outcome} onChange={(e) => setForm({ ...form, desired_outcome: e.target.value })} /></Field>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="sr-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["low", "normal", "high", "urgent"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Requested due date"><Input type="date" data-testid="sr-due" value={form.requested_due_date} onChange={(e) => setForm({ ...form, requested_due_date: e.target.value })} /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="sr-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="text-xs uppercase tracking-widest text-[#57534E]">Services requested *</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {SERVICE_OPTS.map((s) => (
            <label key={s} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${form.services_requested.includes(s) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
              <input type="checkbox" checked={form.services_requested.includes(s)} onChange={() => toggleService(s)} className="accent-[#E8712C]" />{s}
            </label>
          ))}
        </div>
      </div>

      <div className="border border-[#E8E2D9] rounded-2xl p-4 space-y-3 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Assign to (staff)">
            <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "unassigned" ? "" : v, staff_role: v === "unassigned" ? "" : (staff.find((s) => s.id === v)?.role || "") })}>
              <SelectTrigger data-testid="sr-assign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}{s.role ? ` · ${s.role}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Additional instructions"><Input value={form.additional_instructions} onChange={(e) => setForm({ ...form, additional_instructions: e.target.value })} /></Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="text-sm">{label}</Label><div className="mt-1">{children}</div></div>; }
